"use client";

import {
	type Client,
	type ClientOptions,
	createClient,
	type FeedbackInput,
	type FetchFeedbackInput,
	type FetchFeedbackResult,
	type FlowRun,
	type FlowState,
	type OpenFlowOptions as SdkOpenFlowOptions,
	type SubmitFeedbackResult,
} from "@getuserfeedback/sdk";
import { createActionRegistry } from "@getuserfeedback/sdk/internal/action-registry";
import type { ReactElement, ReactNode } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
} from "react";

/**
 * Return value for the experimental `useFeedback()` hook.
 *
 * @experimental
 */
export interface UseFeedbackReturn {
	/** Fetch feedback matching each selector for the JWT-authenticated user. */
	fetch: (input: FetchFeedbackInput) => Promise<FetchFeedbackResult>;
	/** True while one or more fetches from this hook are pending. */
	isFetching: boolean;
	/** True while one or more submissions from this hook are pending. */
	isSubmitting: boolean;
	/** Submit feedback about the current app or a specific target. */
	submit: (input: FeedbackInput) => Promise<SubmitFeedbackResult>;
}

/**
 * Options for useFlow().
 */
export interface UseFlowOptions {
	/** The flow/survey ID to control. */
	flowId: string;
	/** Prefetch the flow resources on mount so it opens faster. Default `false`. */
	prefetchOnMount?: boolean;
	/**
	 * Where the flow mounts: `"default"` (floating container) or `"custom"` (your container via containerRef).
	 * Default `"default"`. Remount the hook to change this mode.
	 */
	container?: "default" | "custom";
	/** When true, the view does not show a close button. */
	hideCloseButton?: boolean;
}

type UseFlowPresentationOptions = Pick<
	UseFlowOptions,
	"container" | "hideCloseButton" | "prefetchOnMount"
>;

/**
 * Per-open options for useFlow(). These apply to the specific response created
 * by that open action and do not affect prerendering.
 */
export interface UseFlowOpenOptions {
	/** Optional response metadata to preserve with the eventual submission. */
	metadata?: SdkOpenFlowOptions["metadata"];
}

/**
 * Return value for useFlow() when container is `"default"`: state and callbacks for the floating flow.
 */
export interface UseFlowReturn {
	readonly container: "default";
	/** True when the flow is visible. */
	isOpen: boolean;
	/** True after open is requested while the flow is not visible yet. */
	isLoading: boolean;
	/** Open the flow. */
	open: (options?: UseFlowOpenOptions) => Promise<void>;
	/** Close the flow. */
	close: () => Promise<void>;
	/** Prefetches the flow resources without opening it. */
	prefetch: () => Promise<void>;
	/** Prerendering warms up the flow for instant display later. */
	prerender: () => Promise<void>;
}

/**
 * Return value for useFlow() when container is `"custom"`: state and callbacks for host-managed container flows.
 */
export interface UseCustomFlowReturn {
	readonly container: "custom";
	/** True when the flow is visible. */
	isOpen: boolean;
	/** True after open is requested while the flow is not visible yet. */
	isLoading: boolean;
	/** True when your host UI should render the custom container element. */
	shouldRenderContainer: boolean;
	/** Open the flow in the custom container. */
	open: (options?: UseFlowOpenOptions) => Promise<void>;
	/** Close the flow. */
	close: () => Promise<void>;
	/** Prefetches the flow resources without opening it. */
	prefetch: () => Promise<void>;
	/**
	 * @deprecated Use `open()` or `close()` instead. Kept for compatibility.
	 */
	setOpen: (next: boolean) => Promise<void>;
	/** Prerendering warms up the flow for instant display later. */
	prerender: () => Promise<void>;
	/** Ref callback: attach this to the container element where the flow should render. */
	containerRef: (element: HTMLDivElement | null) => void;
}

/**
 * Return value for useFlowContainer(): container ref and aggregate flow state
 * for flows using the default presentation in the same logical client
 * (including targeting-engine opens). Mount exactly one consumer per logical
 * client.
 */
export interface UseFlowContainerReturn {
	/** True when any flow is visible. */
	isOpen: boolean;
	/** True after any flow open is requested while the flow is not visible yet. */
	isLoading: boolean;
	/** True when your host UI should render the provider-wide flow container. */
	shouldRenderContainer: boolean;
	/** Close all open flows owned by this logical client. */
	close: () => Promise<void>;
	/** Ref callback for the provider-wide flow container element. */
	containerRef: (element: HTMLDivElement | null) => void;
}

type InstanceFlowRenderState = {
	isOpen: boolean;
	isLoading: boolean;
};

type DefaultFlowContainerState = {
	isOpen: boolean;
	isLoading: boolean;
	shouldRenderContainer: boolean;
};

const DEFAULT_FLOW_CONTAINER_STATE: DefaultFlowContainerState = {
	isOpen: false,
	isLoading: false,
	shouldRenderContainer: false,
};

const toInstanceFlowRenderState = (
	state: FlowState,
): InstanceFlowRenderState => ({
	isOpen: state.isOpen,
	isLoading: state.isLoading,
});

const hasPreparedFlowState = (state: FlowState): boolean =>
	typeof state.width === "number" && typeof state.height === "number";

const isSameFlowLifecycleState = (
	a: Pick<FlowState, "isOpen" | "isLoading" | "width" | "height">,
	b: Pick<FlowState, "isOpen" | "isLoading" | "width" | "height">,
): boolean =>
	a.isOpen === b.isOpen &&
	a.isLoading === b.isLoading &&
	hasPreparedFlowState(a) === hasPreparedFlowState(b);

type FlowStateStore = {
	getFlowState: () => FlowState;
	subscribeFlowState: (
		listener: (state: FlowState) => void,
		options?: { emitInitial?: boolean },
	) => () => void;
};

const FALLBACK_FLOW_STATE: FlowState = {
	isOpen: false,
	isLoading: false,
	width: undefined,
	height: undefined,
};

function useStableFlowStateSnapshot(store: FlowStateStore): () => FlowState {
	const snapshotRef = useRef<FlowState>(store.getFlowState());

	return useCallback((): FlowState => {
		const next = store.getFlowState();
		const previous = snapshotRef.current;
		if (isSameFlowLifecycleState(previous, next)) {
			return previous;
		}
		snapshotRef.current = next;
		return next;
	}, [store]);
}

function useFlowStateStoreValue<TState>(
	store: FlowStateStore,
	project: (state: FlowState) => TState,
	getServerSnapshot: () => TState,
): TState {
	const getFlowStateSnapshot = useStableFlowStateSnapshot(store);
	const lastSourceSnapshotRef = useRef<FlowState | null>(null);
	const lastProjectedSnapshotRef = useRef<TState | null>(null);
	const getProjectedSnapshot = useCallback((): TState => {
		const sourceSnapshot = getFlowStateSnapshot();
		if (lastSourceSnapshotRef.current === sourceSnapshot) {
			const cachedProjectedSnapshot = lastProjectedSnapshotRef.current;
			if (cachedProjectedSnapshot !== null) {
				return cachedProjectedSnapshot;
			}
		}
		const projectedSnapshot = project(sourceSnapshot);
		lastSourceSnapshotRef.current = sourceSnapshot;
		lastProjectedSnapshotRef.current = projectedSnapshot;
		return projectedSnapshot;
	}, [getFlowStateSnapshot, project]);

	return useSyncExternalStore<TState>(
		(onStoreChange) =>
			store.subscribeFlowState(
				() => {
					onStoreChange();
				},
				{ emitInitial: false },
			),
		getProjectedSnapshot,
		getServerSnapshot,
	);
}

type GetUserFeedbackContextValue = {
	client: Client;
	apiKey: string;
	setDefaultFlowContainer: (owner: object, element: HTMLElement | null) => void;
	registerDefaultFlowContainerConsumer: (
		owner: object,
		element: HTMLElement | null,
	) => () => void;
	defaultFlowContainerState: DefaultFlowContainerState;
};

export const GetUserFeedbackContext =
	createContext<GetUserFeedbackContextValue | null>(null);

const flowContainerOwnerByClient = new WeakMap<Client, object>();

export interface GetUserFeedbackProviderProps {
	/** Application subtree that can use getuserfeedback hooks. */
	children: ReactNode;
	/**
	 * Core SDK client options used to create/reuse the underlying client.
	 * Action definitions must remain fixed for the provider's mounted
	 * lifetime; handlers may change while their custom key and version stay
	 * the same.
	 */
	clientOptions: ClientOptions;
}

const toActionDefinitionsKey = (
	actions: ClientOptions["actions"],
): string | null => {
	try {
		return JSON.stringify(
			createActionRegistry(actions).listRegistrationIdentities(),
		);
	} catch {
		return null;
	}
};

/**
 * Provider for getuserfeedback React hooks.
 * Wrap your app (or a subtree) so `useFlow`, `useFlowContainer`, and
 * `useGetUserFeedback` can access a client created from `clientOptions`.
 *
 * If your app uses `useFlowContainer()`, flows using the default presentation
 * in this subtree (including targeting-engine opens) target that container.
 * If the container ref is not mounted yet, loader keeps flows parked until it is.
 * Mount exactly one `useFlowContainer()` consumer per logical client. Providers
 * with the same API key and targeting context reuse that client.
 *
 * @example Basic usage
 * ```tsx
 * function App() {
 *   return (
 *     <GetUserFeedbackProvider clientOptions={{ apiKey: "YOUR_API_KEY" }}>
 *       <Routes />
 *     </GetUserFeedbackProvider>
 *   );
 * }
 * ```
 *
 * @example Display *any* flow in your own dialog (a shadcn/ui dialog in this example):
 * ```tsx
 * function AppFlowContainer() {
 *   const { close, containerRef, shouldRenderContainer } = useFlowContainer();
 *
 *   return (
 *     <Dialog
 *       open={shouldRenderContainer}
 *       onOpenChange={(open) => {
 *         if (!open) {
 *           void close().catch((error) =>
 *             console.error("Unable to close feedback", error),
 *           );
 *         }
 *       }}
 *     >
 *       <DialogContent>
 *         <div ref={containerRef} style={{ width: "100%" }} />
 *       </DialogContent>
 *     </Dialog>
 *   );
 * }
 *
 * function App() {
 *   return (
 *     <GetUserFeedbackProvider clientOptions={{ apiKey: "YOUR_API_KEY" }}>
 *       <AppRoutes />
 *       <AppFlowContainer /> // Targeted flows display in your dialog.
 *     </GetUserFeedbackProvider>
 *   );
 * }
 * ```
 */
export function GetUserFeedbackProvider({
	children,
	clientOptions,
}: GetUserFeedbackProviderProps): ReactElement {
	const initialActionDefinitionsKeyRef = useRef(
		toActionDefinitionsKey(clientOptions.actions),
	);
	const actionDefinitionsKey = toActionDefinitionsKey(clientOptions.actions);
	if (
		actionDefinitionsKey !== null &&
		initialActionDefinitionsKeyRef.current !== null &&
		actionDefinitionsKey !== initialActionDefinitionsKeyRef.current
	) {
		throw new Error(
			"GetUserFeedbackProvider clientOptions.actions definitions cannot change after mount. Keep each custom key and version fixed for the provider lifetime; handler functions may change.",
		);
	}
	const client = createClient(clientOptions);
	const defaultFlowContainerOwnerRef = useRef<object | null>(null);
	const instanceFlowState = useFlowStateStoreValue<InstanceFlowRenderState>(
		client,
		toInstanceFlowRenderState,
		() => DEFAULT_FLOW_CONTAINER_STATE,
	);

	const setDefaultFlowContainer = useCallback(
		(owner: object, element: HTMLElement | null) => {
			if (
				defaultFlowContainerOwnerRef.current !== owner ||
				flowContainerOwnerByClient.get(client) !== owner
			) {
				return;
			}
			client.setDefaultContainerPolicy({
				kind: "hostContainer",
				host: element,
				sharing: "perFlowRun",
			});
		},
		[client],
	);

	const registerDefaultFlowContainerConsumer = useCallback(
		(owner: object, element: HTMLElement | null) => {
			const currentOwner = flowContainerOwnerByClient.get(client);
			if (currentOwner !== undefined && currentOwner !== owner) {
				throw new Error(
					"Only one useFlowContainer consumer can be mounted for a getuserfeedback client",
				);
			}
			flowContainerOwnerByClient.set(client, owner);
			defaultFlowContainerOwnerRef.current = owner;
			client.setDefaultContainerPolicy({
				kind: "hostContainer",
				host: element,
				sharing: "perFlowRun",
			});

			return () => {
				if (
					defaultFlowContainerOwnerRef.current !== owner ||
					flowContainerOwnerByClient.get(client) !== owner
				) {
					return;
				}
				defaultFlowContainerOwnerRef.current = null;
				flowContainerOwnerByClient.delete(client);
				client.setDefaultContainerPolicy({ kind: "floating" });
			};
		},
		[client],
	);

	const defaultFlowContainerState = useMemo<DefaultFlowContainerState>(() => {
		if (!instanceFlowState.isOpen && !instanceFlowState.isLoading) {
			return DEFAULT_FLOW_CONTAINER_STATE;
		}
		return {
			isOpen: instanceFlowState.isOpen,
			isLoading: instanceFlowState.isLoading,
			shouldRenderContainer: true,
		};
	}, [instanceFlowState]);

	const contextValue = useMemo(
		() => ({
			client,
			apiKey: clientOptions.apiKey,
			setDefaultFlowContainer,
			registerDefaultFlowContainerConsumer,
			defaultFlowContainerState,
		}),
		[
			client,
			clientOptions.apiKey,
			defaultFlowContainerState,
			registerDefaultFlowContainerConsumer,
			setDefaultFlowContainer,
		],
	);

	return (
		<GetUserFeedbackContext.Provider value={contextValue}>
			{children}
		</GetUserFeedbackContext.Provider>
	);
}

type FlowControllerRun = Pick<
	FlowRun,
	"setContainer" | "getFlowState" | "subscribeFlowState" | "prefetch" | "close"
>;

type FlowControllerOpenInput = {
	container: HTMLDivElement | null | undefined;
	metadata?: UseFlowOpenOptions["metadata"];
	hideCloseButton?: boolean;
};

export type UseFlowControllerOptions = {
	flowRun: FlowControllerRun;
	mode: "default" | "container";
	openFlow: (input: FlowControllerOpenInput) => Promise<void>;
	prerenderFlow: (input: {
		container: HTMLDivElement | null | undefined;
		hideCloseButton?: boolean;
	}) => Promise<void>;
	prefetchOnMount?: boolean;
	hideCloseButton?: boolean;
};

type UseFlowControllerReturn = {
	isOpen: boolean;
	isLoading: boolean;
	shouldRenderContainer: boolean;
	setOpen: (next: boolean) => Promise<void>;
	open: (options?: UseFlowOpenOptions) => Promise<void>;
	close: () => Promise<void>;
	prefetch: () => Promise<void>;
	prerender: () => Promise<void>;
	containerRef: (element: HTMLDivElement | null) => void;
};

type UseFlowRunOptions = UseFlowPresentationOptions & {
	flowRun: FlowControllerRun;
	openFlow: UseFlowControllerOptions["openFlow"];
	prerenderFlow: UseFlowControllerOptions["prerenderFlow"];
};

const useFlowController = (
	options: UseFlowControllerOptions,
): UseFlowControllerReturn => {
	const {
		flowRun,
		openFlow,
		prerenderFlow,
		prefetchOnMount = false,
		hideCloseButton,
	} = options;
	const requiresContainer = options.mode === "container";
	const customContainerRef = useRef<HTMLDivElement | null>(null);

	/** Flow state from loader events; dimensions are used only for prepared-view lifecycle. */
	const flowState = useFlowStateStoreValue<FlowState>(
		flowRun,
		(state) => state,
		() => FALLBACK_FLOW_STATE,
	);

	const open = useCallback(
		(openOptions?: UseFlowOpenOptions): Promise<void> =>
			openFlow({
				container: requiresContainer
					? (customContainerRef.current ?? null)
					: undefined,
				hideCloseButton,
				metadata: openOptions?.metadata,
			}),
		[hideCloseButton, openFlow, requiresContainer],
	);

	const close = useCallback((): Promise<void> => flowRun.close(), [flowRun]);
	const prefetch = useCallback(
		(): Promise<void> => flowRun.prefetch(),
		[flowRun],
	);

	const setOpen = useCallback(
		(next: boolean): Promise<void> => (next ? open() : close()),
		[close, open],
	);

	const containerRef = useCallback(
		(element: HTMLDivElement | null): void => {
			if (!requiresContainer) {
				return;
			}
			customContainerRef.current = element;
			flowRun.setContainer(element);
		},
		[flowRun, requiresContainer],
	);

	const hasPreparedCustomView = hasPreparedFlowState(flowState);
	const shouldRenderContainer =
		requiresContainer &&
		(flowState.isOpen || (flowState.isLoading && hasPreparedCustomView));

	useEffect(() => {
		if (!prefetchOnMount) {
			return;
		}
		void prefetch().catch(() => {});
	}, [prefetch, prefetchOnMount]);

	return {
		isOpen: flowState.isOpen,
		isLoading: flowState.isLoading,
		shouldRenderContainer,
		setOpen,
		open,
		close,
		prefetch,
		prerender: (): Promise<void> =>
			prerenderFlow({
				container: requiresContainer
					? (customContainerRef.current ?? null)
					: undefined,
				hideCloseButton,
			}),
		containerRef,
	};
};

export function useFlowRun(
	options: UseFlowRunOptions & { container?: "default" },
): UseFlowReturn;
export function useFlowRun(
	options: UseFlowRunOptions & { container: "custom" },
): UseCustomFlowReturn;
export function useFlowRun(
	options: UseFlowRunOptions,
): UseFlowReturn | UseCustomFlowReturn;
export function useFlowRun(
	options: UseFlowRunOptions,
): UseFlowReturn | UseCustomFlowReturn {
	const container = options.container ?? "default";
	const initialContainerRef = useRef(container);
	if (initialContainerRef.current !== container) {
		throw new Error(
			"useFlow container mode cannot change while mounted; remount the hook to change it",
		);
	}
	const mode = container === "custom" ? "container" : "default";
	const controller = useFlowController({
		flowRun: options.flowRun,
		mode,
		openFlow: options.openFlow,
		prerenderFlow: options.prerenderFlow,
		prefetchOnMount: options.prefetchOnMount,
		hideCloseButton: options.hideCloseButton,
	});

	if (mode === "container") {
		return {
			container: "custom",
			isOpen: controller.isOpen,
			isLoading: controller.isLoading,
			shouldRenderContainer: controller.shouldRenderContainer,
			open: controller.open,
			close: controller.close,
			prefetch: controller.prefetch,
			setOpen: controller.setOpen,
			prerender: controller.prerender,
			containerRef: controller.containerRef,
		};
	}

	return {
		container: "default",
		isOpen: controller.isOpen,
		isLoading: controller.isLoading,
		open: controller.open,
		close: controller.close,
		prefetch: controller.prefetch,
		prerender: controller.prerender,
	};
}

/**
 * @name useFlow
 * @description Open a flow in the default container or your own container.
 * @example Default container
 * ```tsx
 * const { open, isLoading } = useFlow({ flowId: "sur_123" });
 * await open({
 *   metadata: {
 *     tags: {
 *       journey_stage: "onboarding",
 *     },
 *   },
 * });
 * ```
 * @example Custom container
 * ```tsx
 * const { containerRef, shouldRenderContainer, open } = useFlow({ flowId: "sur_123", container: "custom" });
 * ```
 * @see https://getuserfeedback.com/docs/guides/advanced/containers
 */
export function useFlow(
	options: UseFlowOptions & { container?: "default" },
): UseFlowReturn;
export function useFlow(
	options: UseFlowOptions & { container: "custom" },
): UseCustomFlowReturn;
export function useFlow(
	options: UseFlowOptions,
): UseFlowReturn | UseCustomFlowReturn {
	const ctx = useContext(GetUserFeedbackContext);
	if (!ctx) {
		throw new Error("useFlow must be used within a GetUserFeedbackProvider");
	}
	const flowRun = useMemo(
		() => ctx.client.flow(options.flowId),
		[ctx.client, options.flowId],
	);
	const openFlow = useCallback(
		({ container, hideCloseButton, metadata }: FlowControllerOpenInput) =>
			flowRun.open(
				container === undefined
					? { hideCloseButton, metadata }
					: { container, hideCloseButton, metadata },
			),
		[flowRun],
	);
	const prerenderFlow = useCallback(
		({ hideCloseButton }: { hideCloseButton?: boolean }) =>
			flowRun.prerender({ hideCloseButton }),
		[flowRun],
	);
	return useFlowRun({
		flowRun,
		openFlow,
		prerenderFlow,
		container: options.container,
		prefetchOnMount: options.prefetchOnMount,
		hideCloseButton: options.hideCloseButton,
	});
}

/**
 * @name useFlowContainer
 * @description Register and control the single default container for this provider subtree. Mount exactly one consumer per logical client.
 * @example Display any flow in your own dialog
 * ```tsx
 * function AppFlowContainer() {
 *   const { close, containerRef, shouldRenderContainer } = useFlowContainer();
 *   return (
 *     <Dialog
 *       open={shouldRenderContainer}
 *       onOpenChange={(open) => {
 *         if (!open) {
 *           void close().catch((error) =>
 *             console.error("Unable to close feedback", error),
 *           );
 *         }
 *       }}
 *     >
 *       <DialogContent>
 *         <div ref={containerRef} style={{ width: "100%" }} />
 *       </DialogContent>
 *     </Dialog>
 *   );
 * }
 *
 * function App() {
 *   return (
 *     <GetUserFeedbackProvider clientOptions={{ apiKey: "YOUR_API_KEY" }}>
 *       <AppRoutes />
 *       <AppFlowContainer />
 *     </GetUserFeedbackProvider>
 *   );
 * }
 * ```
 * @see https://getuserfeedback.com/docs/guides/advanced/containers
 */
export function useFlowContainer(): UseFlowContainerReturn {
	const ctx = useContext(GetUserFeedbackContext);
	if (!ctx) {
		throw new Error(
			"useFlowContainer must be used within a GetUserFeedbackProvider",
		);
	}

	const {
		client,
		defaultFlowContainerState,
		registerDefaultFlowContainerConsumer,
		setDefaultFlowContainer,
	} = ctx;
	const ownerRef = useRef<object>({});
	const containerElementRef = useRef<HTMLDivElement | null>(null);

	useEffect(
		() =>
			registerDefaultFlowContainerConsumer(
				ownerRef.current,
				containerElementRef.current,
			),
		[registerDefaultFlowContainerConsumer],
	);

	const containerRef = useCallback(
		(element: HTMLDivElement | null): void => {
			containerElementRef.current = element;
			setDefaultFlowContainer(ownerRef.current, element);
		},
		[setDefaultFlowContainer],
	);

	const close = useCallback((): Promise<void> => client.close(), [client]);

	return {
		isOpen: defaultFlowContainerState.isOpen,
		isLoading: defaultFlowContainerState.isLoading,
		shouldRenderContainer: defaultFlowContainerState.shouldRenderContainer,
		close,
		containerRef,
	};
}

/**
 * Experimental hook for submitting feedback or explicitly fetching the
 * JWT-authenticated user's feedback.
 *
 * Neither operation runs automatically. Each promise returns the corresponding
 * SDK result and rejects when the operation fails.
 *
 * @experimental
 */
export function useFeedback(): UseFeedbackReturn {
	const ctx = useContext(GetUserFeedbackContext);
	if (!ctx) {
		throw new Error(
			"useFeedback must be used within a GetUserFeedbackProvider",
		);
	}

	const [pendingFetchCount, setPendingFetchCount] = useState(0);
	const [pendingSubmissionCount, setPendingSubmissionCount] = useState(0);
	const fetch = useCallback(
		async (input: FetchFeedbackInput): Promise<FetchFeedbackResult> => {
			setPendingFetchCount((count) => count + 1);
			try {
				return await ctx.client.feedback.fetch(input);
			} finally {
				setPendingFetchCount((count) => count - 1);
			}
		},
		[ctx.client],
	);
	const submit = useCallback(
		async (input: FeedbackInput): Promise<SubmitFeedbackResult> => {
			setPendingSubmissionCount((count) => count + 1);
			try {
				return await ctx.client.feedback.submit(input);
			} finally {
				setPendingSubmissionCount((count) => count - 1);
			}
		},
		[ctx.client],
	);

	return {
		fetch,
		isFetching: pendingFetchCount > 0,
		isSubmitting: pendingSubmissionCount > 0,
		submit,
	};
}

/**
 * Hook for accessing the getuserfeedback client instance.
 *
 * @see https://getuserfeedback.com/docs/reference/react-sdk-reference
 * @see https://getuserfeedback.com/docs/reference/events
 *
 * @example Basic usage
 * ```tsx
 * const client = useGetUserFeedback();
 *
 * async function handleLogin() {
 *   await client.identify("user-123", { email: "user@example.com" });
 * }
 *
 * async function handleCheckoutStarted() {
 *   await client.track("Checkout Started", { plan: "pro" });
 * }
 *
 * async function handleLogout() {
 *   await client.reset();
 * }
 * ```
 *
 * @example Override color scheme
 * ```tsx
 * const client = useGetUserFeedback();
 * await client.configure({ colorScheme: "dark" });
 * ```
 */
export function useGetUserFeedback(): Client {
	const ctx = useContext(GetUserFeedbackContext);
	if (!ctx) {
		throw new Error(
			"useGetUserFeedback must be used within a GetUserFeedbackProvider",
		);
	}
	return ctx.client;
}
